import {isNil, reject} from 'lodash';
import {NextRequest} from 'next/server';
import {UAParser} from 'ua-parser-js';
import {LineOffer} from '../purchase-public-service/types';

export const CLIENT_NAME_MAP: Record<string, string> = {
	'fgc-ios': 'iOS-Store-FGCMobile',
	'fgc-android': 'Android-Store-FGCMobile'
};

type PurchaseLineOffer = LineOffer & {membershipId?: string};

/**
 * Creates a LineOffer from a namespace and a offer id, defaults to quantity 1
 * @see epic-launcher-integration/app/scripts/utils/WebPurchaseIntegration.js#buildPurchaseUrl
 * NOTE: user can only purchase 1 legacy offer
 */
export const parseLegacyOffer = (namespace?: string, offerStr?: string): LineOffer | undefined => {
	if (namespace && offerStr) {
		return {
			quantity: 1,
			namespace,
			offerId: offerStr
		};
	}
	return undefined;
};

// FIXME: potential problem with private sandboxes where the name starts with 'p-'
/**
 * Creates a Line Offer from a v2 line item string. Expected string format is '{quantity}-{namespace}-{offer id}'
 * epic-launcher-integration/app/scripts/utils/WebPurchaseIntegration.js#buildPurchaseUrlV2
 */
export const parseOffer = (
	offerStr?: string,
	namespace?: string
): PurchaseLineOffer | undefined => {
	if (offerStr && offerStr.includes('-')) {
		const [quantity, namespace, offerId, membershipId] = offerStr.split('-');
		return {
			quantity: parseInt(quantity),
			namespace,
			offerId,
			membershipId: membershipId || undefined
		};
	}
	return parseLegacyOffer(namespace, offerStr);
};

/**
 * Entry point for parsing a query string that is provided to the PurchaseHandler.
 * Will accept either v1 or v2 query params and return an array of LineOffers
 */
export const parseOffersQuery = (req: NextRequest): LineOffer[] => {
	const searchParams = req.nextUrl.searchParams;
	const offers = searchParams.getAll('offers');
	const namespace = searchParams.get('namespace')!; // v1 only prop

	if (!offers || !offers.length) {
		return [];
	}

	const lineOffers = offers.map((offerStr) => parseOffer(offerStr, namespace));
	return reject(lineOffers, isNil) as LineOffer[];
};

// salesChannel only allows alphabet numeric, dash and underline, with a limit of max 128 characters. fallback to default if it is invalid.
export const isValidSalesChannel = (salesChannel: string | null): salesChannel is string => {
	return !!salesChannel && new RegExp(/^[a-zA-Z0-9_-]{1,128}$/).test(salesChannel);
};

export const getSalesChannel = (req: NextRequest): string => {
	const searchParams = req.nextUrl.searchParams;
	const salesChannel = searchParams.get('salesChannel');
	const clientName = searchParams.get('clientName');

	if (clientName && CLIENT_NAME_MAP[clientName]) {
		return CLIENT_NAME_MAP[clientName];
	}

	if (isValidSalesChannel(salesChannel)) {
		return salesChannel;
	}

	const agent = UAParser(req.headers.get('user-agent')!);
	// remove space. e.g. Mac OS -> MacOS
	const os = agent.os.name?.replace(/\s+/g, '') || 'unknown';

	return `${os}-Store-FGCWeb`;
};

export const getMerchantGroup = (req: NextRequest) => {
	const searchParams = req.nextUrl.searchParams;
	// currently, merchantGroup only allows 'FN_MKT'. fallback to default if it is invalid.
	if (searchParams.get('merchantGroup') === 'FN_MKT') {
		return 'FN_MKT';
	}
	return process.env.EPIC_PAYMENT_MERCHANT_GROUP;
};
